import React, { useState, useEffect } from 'react';
import { 
  FaLink, 
  FaEye, 
  FaTrash,
  FaFilter,
  FaExternalLinkAlt
} from 'react-icons/fa';

const AdminShortLinks = () => {
  const [shortLinks, setShortLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const fetchShortLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const shortLinksRes = await fetch(`/api/dashboard/admin/shortlinks?page=${currentPage}&limit=10`, { headers });
      if (shortLinksRes.ok) {
        const shortLinksData = await shortLinksRes.json();
        setShortLinks(shortLinksData.shortLinks);
        setTotalPages(shortLinksData.totalPages);
      }
    } catch (error) {
      console.error('Error fetching short links:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchShortLinks();
  }, [currentPage]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleViewShortLink = (shortLink) => {
    window.open(`/s/${shortLink.shortCode}`, '_blank');
  };

  const handleDeleteShortLink = (shortLinkId, title) => {
    setDeleteTargetId(shortLinkId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const response = await fetch(`/api/dashboard/admin/shortlinks/${deleteTargetId}`, {
        method: 'DELETE',
        headers
      });

      if (response.ok) {
        setShortLinks(prev => prev.filter(link => link._id !== deleteTargetId));
        setShowDeleteModal(false);
        setDeleteTargetId(null);
      }
    } catch (error) {
      console.error('Error deleting short link:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="bg-gray-800 border border-gray-700 rounded-xl">
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Short Link</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Kullanıcı</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Tıklama</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Oluşturulma</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {shortLinks.map((link) => (
                  <tr key={link._id} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="flex items-center">
                        <FaLink className="h-5 w-5 text-indigo-400 mr-3" />
                        <div>
                          <div className="text-sm font-medium text-white">{link.shortCode}</div>
                          <div className="text-xs text-gray-400 truncate max-w-xs">{link.originalUrl}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="text-sm text-white font-medium">
                        {link.userId?.firstName} {link.userId?.lastName}
                      </div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="text-sm text-white font-medium">{link.clickCount || 0}</div>
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-400 text-left">
                      {formatDate(link.createdAt)}
                    </td>
                    <td className="px-6 py-5 whitespace-nowrap text-left">
                      <div className="flex items-center justify-start space-x-3">
                        <button 
                          onClick={() => handleViewShortLink(link)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-blue-900/20 rounded-lg transition-colors"
                          title="Görüntüle"
                        >
                          <FaEye className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => window.open(`/s/${link.shortCode}`, '_blank')}
                          className="p-2 text-green-400 hover:text-green-300 hover:bg-green-900/20 rounded-lg transition-colors"
                          title="Aç"
                        >
                          <FaExternalLinkAlt className="h-4 w-4" />
                        </button>
                        <button 
                          onClick={() => handleDeleteShortLink(link._id, link.shortCode)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <FaTrash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
              >
                Önceki
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-600 text-sm font-medium rounded-lg text-gray-300 bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
              >
                Sonraki
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-400">
                  Sayfa <span className="font-medium text-white">{currentPage}</span> /{' '}
                  <span className="font-medium text-white">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-lg shadow-sm -space-x-px">
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    Önceki
                  </button>
                  <button
                    onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-600 bg-gray-700 text-sm font-medium text-gray-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    Sonraki
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Short Link Sil</h3>
            <p className="text-gray-300 mb-6">
              Bu short link'i silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                İptal
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Sil
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminShortLinks;
